import api from './api';
import { API_ROUTES } from '@/lib/constants';
import { createCrudService } from './crud.factory';
import type { Quotation, Invoice, ApiResponse } from '@/types/table';

/**
 * FULL: the whole quotation as quoted (only while nothing is invoiced).
 * PARTIAL: picked quotation lines and/or free lines; amounts are pre-tax and
 * the quotation's tax rate applies. The server refuses anything that would
 * take the quotation past its total.
 */
export interface CreateQuotationInvoicePayload {
  mode: 'FULL' | 'PARTIAL';
  issueDate?: string;
  dueDate?: string;
  quotationItemIds?: string[];
  lines?: Array<{ description: string; amount: number }>;
  discountAmount?: number;
}

export const quotationService = {
  ...createCrudService<Quotation>(API_ROUTES.QUOTATIONS),

  send: async (id: string): Promise<ApiResponse<Quotation>> => {
    const response = await api.post(`${API_ROUTES.QUOTATIONS}/${id}/send`);
    return response.data;
  },

  /** Accept the quotation. Records the client's yes only — invoices are raised with createInvoice. */
  accept: async (id: string): Promise<ApiResponse<Quotation>> => {
    const response = await api.post(`${API_ROUTES.QUOTATIONS}/${id}/accept`);
    return response.data;
  },

  /** Raise a draft invoice against an accepted quotation, in full or in part. */
  createInvoice: async (id: string, data: CreateQuotationInvoicePayload): Promise<ApiResponse<Invoice>> => {
    const response = await api.post(`${API_ROUTES.QUOTATIONS}/${id}/invoices`, data);
    return response.data;
  },

  reject: async (id: string): Promise<ApiResponse<Quotation>> => {
    const response = await api.post(`${API_ROUTES.QUOTATIONS}/${id}/reject`);
    return response.data;
  },

  cancel: async (id: string): Promise<ApiResponse<Quotation>> => {
    const response = await api.post(`${API_ROUTES.QUOTATIONS}/${id}/cancel`);
    return response.data;
  },

  duplicate: async (id: string): Promise<ApiResponse<Quotation>> => {
    const response = await api.post(`${API_ROUTES.QUOTATIONS}/${id}/duplicate`);
    return response.data;
  },

  /** Render the form's unsaved state as a PDF. Pass the id when previewing an edit. */
  preview: async (data: Record<string, unknown>, id?: string): Promise<Blob> => {
    const response = await api.post(`${API_ROUTES.QUOTATIONS}/preview`, data, { responseType: 'blob', params: id ? { id } : undefined });
    return response.data;
  },

  downloadPdf: async (id: string): Promise<Blob> => {
    const response = await api.get(`${API_ROUTES.QUOTATIONS}/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },

  getNextNumber: async (): Promise<ApiResponse<{ nextNumber: string }>> => {
    const response = await api.get(`${API_ROUTES.QUOTATIONS}/next-number`);
    return response.data;
  },
};
